import { CheckboxAtom, colors, Stack, Typography } from '@ui-components/neo.tax';
import { CheckboxOptions } from 'ariakit';
import React, { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';

import { useAscContext } from '@neo.tax/asc/context';
import { useAscProjectGroupingTicketsOrCollections, type AscProject } from '@neo.tax/asc/hooks';
import {
  AscClusterType,
  AscGroupingTicketOrCollection,
  KeywordSearch,
  useKeywordSearch,
  GroupedTicketsComponentProps,
  TicketGroupingKind,
  TicketGroupingList,
} from '@neo.tax/shared';

import { Footer, MainContainer, StackFlexShrink } from './asc-tickets-pane.styled';
import { AscGroupedTickets } from './AscGroupedTickets';
import { GroupingSelection, useTicketSelections } from './hooks';
import { MoveAscTicketsButton } from './MoveAscTicketsButton';

export interface AscTicketsPaneProps {
  className?: string;
  ascProject: AscProject;
}

export const AscTicketsPane = ({ ascProject, className }: AscTicketsPaneProps): ReactElement => {
  const { searchKeyword, setSearchKeyword } = useKeywordSearch();
  const [groupingSelections, setGroupingSelections] = useState<Record<string, GroupingSelection>>({});
  const { fiscalYearId, periodName } = useAscContext();

  const { data, loading } = useAscProjectGroupingTicketsOrCollections({
    ascProjectId: ascProject.id,
    fiscalYearId,
    periodName,
    searchKeyword,
  });

  // Reset grouping selections when ascProject.id or searchKeyword changes
  useEffect(() => {
    setGroupingSelections({});
  }, [ascProject.id, searchKeyword]);

  const selectedGroupingsCount = useMemo(
    () => Object.values(groupingSelections).filter((selection) => selection.isGroupingSelected).length,
    [groupingSelections],
  );

  // Because we've deselected non-matching tickets, we can determine if they're
  // all selected by just checking the sizes of the collections. This can be
  // true, false, or 'mixed' if only some are selected.
  const selectAllChecked: CheckboxOptions['checked'] =
    selectedGroupingsCount === 0 ? false : selectedGroupingsCount === data?.length ? true : 'mixed';

  const doSelectAll = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.checked) {
        // If we're checking the box, select all grouping tickets.
        const newSelections: Record<string, GroupingSelection> = {};
        data?.forEach((groupingTicketOrCollection) => {
          newSelections[groupingTicketOrCollection.id] = {
            isGroupingSelected: true,
            selectedTicketIds: new Set(),
          };
        });
        setGroupingSelections(newSelections);
      } else {
        // If we're un-checking the box, clear all selections.
        setGroupingSelections({});
      }
    },
    [data],
  );

  const selectedTicketCount = useMemo(() => {
    if (!data) return 0;

    let totalCount = 0;

    data.forEach((grouping) => {
      const selection = groupingSelections[grouping.id];
      if (selection?.isGroupingSelected) {
        // If whole grouping is selected, count all tickets in the grouping
        totalCount += grouping.count;
      } else if (selection?.selectedTicketIds.size > 0) {
        // If individual tickets are selected, count only those
        totalCount += selection.selectedTicketIds.size;
      }
    });

    return totalCount;
  }, [data, groupingSelections]);

  const { groupings, individualTicketIds, selectedItemsSummary } = useTicketSelections({
    data: data ?? [],
    groupingSelections,
  });

  // Order the (non-paginated) grouping list: new matches first, then existing matches, then the
  // NO_PARENT bucket last. Stable sort preserves the SQL within-bucket order (count desc, etc.).
  const orderedGroupings = useMemo(() => {
    const rank = (grouping: AscGroupingTicketOrCollection): number => {
      if (grouping.isMatchedInLatestSync) return 0;
      if (grouping.kind === TicketGroupingKind.NoParent) return 2;
      return 1;
    };
    return [...(data ?? [])].sort((a, b) => rank(a) - rank(b));
  }, [data]);

  const BoundAscGroupedTickets = useMemo(
    () =>
      function BoundAscGroupedTickets(props: GroupedTicketsComponentProps): ReactElement {
        return (
          <AscGroupedTickets
            {...props}
            ascProjectId={ascProject.id}
            groupingTicketOrCollection={props.groupingTicketOrCollection as AscGroupingTicketOrCollection}
            isNoiseProject={ascProject.clusterType === AscClusterType.CapitalizableProjectNoise}
          />
        );
      },
    [ascProject.id, ascProject.clusterType],
  );

  return (
    <MainContainer className={className} direction="column">
      <StackFlexShrink paddingX={4} paddingY={3} gap={3}>
        <KeywordSearch searchKeyword={searchKeyword} setSearchKeyword={setSearchKeyword} />
      </StackFlexShrink>
      <TicketGroupingList
        GroupedTicketsComponent={BoundAscGroupedTickets}
        groupingTicketOrCollections={orderedGroupings}
        groupingSelections={groupingSelections}
        setGroupingSelections={setGroupingSelections}
        loading={loading}
        searchKeyword={searchKeyword}
        isMoveTicketsDisabled={false}
        isNoiseProject={ascProject.clusterType === AscClusterType.CapitalizableProjectNoise}
      />
      {selectedItemsSummary.hasSelections && (
        <StackFlexShrink backgroundColor={colors.gray[600]} paddingX={4} paddingY={1}>
          <Typography fontSize="xs" color={colors.gray[100]} textAlign="center">
            {selectedItemsSummary.text}
          </Typography>
        </StackFlexShrink>
      )}
      <Footer
        backgroundColor={colors.gray[500]}
        direction="row"
        justifyContent="space-between"
        paddingX={4}
        paddingY={4}
      >
        <Stack as="label" alignItems="center" gap={2}>
          {!searchKeyword && (
            <>
              <CheckboxAtom checked={selectAllChecked} onChange={doSelectAll} />
              <Typography fontWeight="medium" color={colors.white}>
                Select All
              </Typography>
            </>
          )}
        </Stack>
        <MoveAscTicketsButton
          groupings={groupings}
          ticketIds={individualTicketIds}
          fromAscProjectId={ascProject.id}
          ticketCount={selectedTicketCount}
          onCompleted={() => setGroupingSelections({})}
        />
      </Footer>
    </MainContainer>
  );
};
